

import { useQuery } from "@tanstack/react-query";
import type { StoreSubscriptionListResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseStoreSubscriptionsQueryParams = {
  limit?: number;
  offset?: number;
  overdue?: boolean;
  planId?: string;
};

type NormalizedFilters = {
  limit?: number;
  offset?: number;
  overdue?: true;
  planId?: string;
};

function normalizeFilters(
  params: UseStoreSubscriptionsQueryParams,
): NormalizedFilters {
  const normalizedPlanId = params.planId?.trim();
  return {
    limit: params.limit,
    offset: params.offset,
    overdue: params.overdue ? true : undefined,
    planId:
      normalizedPlanId && normalizedPlanId.length > 0 ? normalizedPlanId : undefined,
  };
}

function buildPath(filters: NormalizedFilters) {
  const query = new URLSearchParams();
  if (filters.limit !== undefined) {
    query.set("limit", String(filters.limit));
  }
  if (filters.offset !== undefined) {
    query.set("offset", String(filters.offset));
  }
  if (filters.overdue) {
    query.set("overdue", "true");
  }
  if (filters.planId) {
    query.set("planId", filters.planId);
  }

  const queryString = query.toString();
  return queryString.length > 0
    ? `${endpoints.admin.storeSubscriptions}?${queryString}`
    : endpoints.admin.storeSubscriptions;
}

export function useStoreSubscriptionsQuery(
  params: UseStoreSubscriptionsQueryParams = {},
) {
  const normalizedFilters = normalizeFilters(params);

  const query = useQuery({
    queryKey: queryKeys.admin.storeSubscriptions(normalizedFilters),
    queryFn: () =>
      apiJsonClient<StoreSubscriptionListResponse>(buildPath(normalizedFilters)),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
